export const TimeResolutionEnums = Object.freeze({
    SECOND: 'second',
    MINUTE: 'minute',
    HOUR: 'hour',
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month',
    YEAR: 'year',
})

export function parseResolution(resolution) {
    const sizeMatch = resolution.match(/^(\d+)/);
    const periodMatch = resolution.match(/[A-Za-z]+$/);

    if (sizeMatch && periodMatch) {
        let periodText = '';
        const size = parseInt(sizeMatch[1]);
        const period = periodMatch[0].toLowerCase();
        switch (period) {
            case 's':
                periodText = TimeResolutionEnums.SECOND;
                break;
            case 'h':
                periodText = TimeResolutionEnums.HOUR;
                break;
            case 'd':
                periodText = TimeResolutionEnums.DAY;
                break;
            case 'w':
                periodText = TimeResolutionEnums.WEEK;
                break;
            case 'm':
                periodText = TimeResolutionEnums.MONTH;
                break;
            default:
                break;
        }
        return [size, periodText];
    }

    if (sizeMatch) {
        const size = parseInt(sizeMatch[1]);
        return [size, TimeResolutionEnums.MINUTE];
    }

    return null;
}


export function getCandleStartTimeFromResolution(resolution) {
    let time = new Date(Date.now());
    time.setMilliseconds(0);
    time.setSeconds(0);

    const parsedResolution = parseResolution(resolution);
    if (parsedResolution === null) {
        throw new Error(`${resolution} Resolution is of unknown type`);
    }

    const [size, resolutionPeriod] = parsedResolution;

    switch (resolutionPeriod) {
        case TimeResolutionEnums.MINUTE:
            time.setSeconds(0);
            time.setMinutes(Math.floor(time.getMinutes() / size) * size);
            break;

        case TimeResolutionEnums.HOUR:
            time.setSeconds(0);
            time.setMinutes(0);
            time.setHours(Math.floor(time.getHours() / size) * size);
            break;

        case TimeResolutionEnums.DAY:
            time.setSeconds(0);
            time.setMinutes(0);
            time.setHours(0);
            break;

        case TimeResolutionEnums.WEEK:
            time.setSeconds(0);
            time.setMinutes(0);
            time.setHours(0);
            time.setDate(time.getDate() - time.getDay() + (time.getDay() === 0 ? -6 : 1));
            break;

        case TimeResolutionEnums.MONTH:
            time.setSeconds(0);
            time.setMinutes(0);
            time.setHours(0);
            time.setDate(1);
            break;
        default:
            break;
    }

    return time;
}

export function stringToMilliseconds(resolution) {
    switch (resolution) {
        case '1S':
            return 1000; // 1 Second resolution
        case '2S':
            return 2 * 1000; // 2 Seconds resolution
        case '5S':
            return 5 * 1000; // 5 Seconds resolution
        case '1':
            return 60 * 1000; // Minutes resolution
        case '5':
            return 5 * 60 * 1000; // 5 Minutes resolution
        case '15':
            return 15 * 60 * 1000; // 15 Minutes resolution
        case '1D':
            return 24 * 60 * 60 * 1000; // Daily resolution
        case '1W':
            return 7 * 24 * 60 * 60 * 1000; // Weekly resolution
        case '1M':
            return 30 * 24 * 60 * 60 * 1000; // Monthly resolution
        default:
            throw new Error(`Unknown resolution type: ${resolution}`);
    }
}


export function formatTimestamp(timestamp) {
    const date = new Date(timestamp);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00.000Z`;
}

// 28-07-2025%2000%3A00%3A00.000 time stamp format
export function getEncodedCurrentDate() {
  return encodeURIComponent(
    `${String(new Date().getDate()).padStart(2, '0')}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${new Date().getFullYear()} ${String(new Date().getHours()).padStart(2, '0')}:${String(new Date().getMinutes()).padStart(2, '0')}:${String(new Date().getSeconds()).padStart(2, '0')}.000`
  );
}

// 28-07-2025%2000%3A00%3A00.000  time stamp format
export function getEncodedPreviousDate() {
  const now = new Date();
  now.setDate(now.getDate() - 2);

  return encodeURIComponent(
    `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}.000`
  );
}



export function parseFormattedDate(formattedDate) {
    const regex = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?/;
    const match = formattedDate.match(regex);

    if (match) {
        const year = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1; // Months are 0-indexed
        const day = parseInt(match[3], 10);
        const hours = parseInt(match[4], 10);
        const minutes = parseInt(match[5], 10);
        const date = Date.UTC(year, month, day, hours, minutes, 0, 0);
        return date;
    } else {
        throw new Error("Invalid formatted date string");
    }
}


export function timestampToDate(unixTimestamp) {
    const date = new Date(unixTimestamp * 1000);
    return date;
}


export function isMarketOpen(scheduleString, datetime) {
    const dayIndex = datetime.getDay(); // 0 for Sunday
    const daySchedule = scheduleString.charAt(dayIndex);

    if (daySchedule === '-') {
        // Market is closed on this day
        return false;
    } else if (daySchedule === '0') {
        // Market is open, but datetime is before opening time
        const openingTime = new Date(datetime);
        openingTime.setHours(parseInt(scheduleString.substr(0, 2), 10));
        openingTime.setMinutes(parseInt(scheduleString.substr(2, 2), 10));
        return datetime >= openingTime;
    } else if (daySchedule === '1') {
        // Market is open, datetime is within opening hours
        const openingTime = new Date(datetime);
        openingTime.setHours(parseInt(scheduleString.substr(0, 2), 10));
        openingTime.setMinutes(parseInt(scheduleString.substr(2, 2), 10));
        const closingTime = new Date(datetime);
        closingTime.setHours(parseInt(scheduleString.substr(5, 2), 10));
        closingTime.setMinutes(parseInt(scheduleString.substr(7, 2), 10));
        return datetime >= openingTime && datetime <= closingTime;
    }
    return false;
}


function removeSecondsFromDatetime(datetime) {
    const datetimeWithoutSeconds = new Date(datetime);
    datetimeWithoutSeconds.setSeconds(0);
    datetimeWithoutSeconds.setMilliseconds(0);
    return datetimeWithoutSeconds;
}

export function findIndexByDatetime(datetimeToFind, list) {
    const targetDatetime = removeSecondsFromDatetime(datetimeToFind);
    for (let i = 0; i < list.length; i++) {
        const mapDatetime = removeSecondsFromDatetime(list[i].date);

        if (targetDatetime.getTime() === mapDatetime.getTime()) {
            return i;
        }
    }
    return -1;
}



export function getHistoricalDateByScrollIndex(index) {
    const result = [];
    let currentDate = new Date();
    currentDate.setMilliseconds(0); // Zero ms for consistency

    while (result.length <= index + 1) {
        const day = currentDate.getDay(); // 0 = Sun, 6 = Sat

        // Skip Saturday (6) and Sunday (0)
        if (day !== 0 && day !== 6) {
            result.push(new Date(currentDate));
        }

        // Always move back one day
        currentDate.setDate(currentDate.getDate() - 1);
    }

    return {
        toDate: result[index],
        fromDate: result[index + 1]
    };
}




export function WaveformatAndEncodeDate(dateData) {
    const date = new Date(dateData);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00.000Z`;
}



export function formatAndEncodeDate(date) {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');

    const formatted = `${dd}-${mm}-${yyyy} ${hh}:${mi}:${ss}.000`;
    return encodeURIComponent(formatted);
}

